import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/features/auth/AuthContext';
import { UserForm } from './UserForm';
import { PasswordDialog } from './PasswordDialog';

export type Role = { id: number; name: string; label: string; description: string | null };

export type ManagedUser = {
  id: number;
  name: string;
  email: string;
  status: string;
  last_login_at: string | null;
  locked_until: string | null;
  roles: Array<{ id: number; name: string; label: string }>;
  entity_access: Array<{
    entity_id: number;
    entity_name: string;
    access_level: string;
    can_see_private: boolean;
  }>;
};

type UserListResponse = {
  data: ManagedUser[];
  meta: { roles: Role[]; current_user_id: number };
};

const STATUS_STYLE: Record<string, { chip: string; label: string }> = {
  ativo: { chip: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300', label: 'Ativo' },
  inativo: { chip: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400', label: 'Inativo' },
  bloqueado: { chip: 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300', label: 'Bloqueado' },
  convidado: { chip: 'bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300', label: 'Convidado' },
};

/**
 * Usuários — RF-AUT.
 *
 * A tela mostra as DUAS camadas lado a lado em cada linha: o papel (o que pode
 * fazer) e as entidades (sobre quais dados). Separá-las em telas diferentes
 * esconderia o caso mais comum de configuração errada — papel generoso com
 * acesso a nada, que produz alguém que entra e vê o sistema vazio.
 */
export function UsersPage() {
  const { user: current } = useAuth();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ManagedUser | null>(null);
  const [resetting, setResetting] = useState<ManagedUser | null>(null);
  const [ownPassword, setOwnPassword] = useState(false);
  const queryClient = useQueryClient();

  const list = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<UserListResponse>('/users'),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['users'] });

  const remove = useMutation({
    mutationFn: (id: number) => api.delete<{ message: string }>(`/users/${id}`),
    onSuccess: (res) => {
      invalidate();
      alert(res.message);
    },
    onError: (e) => alert(e instanceof ApiError ? e.message : 'Não foi possível excluir.'),
  });

  const users = list.data?.data ?? [];
  const roles = list.data?.meta.roles ?? [];

  return (
    <div className="mx-auto max-w-5xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Usuários</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-500 dark:text-slate-400">
            Cada usuário precisa de <strong>papel</strong> e <strong>acesso a entidades</strong>. O
            papel diz o que ele pode fazer; a entidade diz sobre quais dados — sem as duas, ele entra
            e não vê nada.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setOwnPassword(true)}
            className="rounded-lg border border-slate-300 px-3.5 py-2 text-sm transition hover:border-indigo-500 hover:text-indigo-600 dark:border-slate-700"
          >
            Trocar minha senha
          </button>
          <button
            onClick={() => setCreating(true)}
            className="rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-indigo-700"
          >
            + Novo usuário
          </button>
        </div>
      </div>

      {list.isPending ? (
        <div className="mt-5 animate-pulse space-y-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="h-24 rounded-xl bg-slate-100 dark:bg-slate-800" />
          ))}
        </div>
      ) : list.isError ? (
        <p className="mt-5 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300">
          {list.error instanceof ApiError && list.error.status === 403
            ? 'Você não tem permissão para administrar usuários.'
            : 'Não foi possível carregar a lista.'}
        </p>
      ) : (
        <div className="mt-5 space-y-2">
          {users.map((u) => {
            const isSelf = u.id === current?.id;
            const style = STATUS_STYLE[u.status] ?? STATUS_STYLE.inativo!;

            return (
              <div
                key={u.id}
                className="rounded-xl border border-slate-200 bg-white p-3.5 dark:border-slate-800 dark:bg-slate-900"
              >
                <div className="flex flex-wrap items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{u.name}</span>
                      {isSelf && (
                        <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium uppercase text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300">
                          você
                        </span>
                      )}
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${style.chip}`}>
                        {style.label}
                      </span>
                      {u.locked_until && (
                        <span className="rounded bg-rose-50 px-1.5 py-0.5 text-[10px] font-medium uppercase text-rose-700 dark:bg-rose-950/40 dark:text-rose-300">
                          travado por tentativas
                        </span>
                      )}
                    </div>

                    <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">{u.email}</p>

                    <p className="mt-1.5 text-xs">
                      <span className="text-slate-400">Papel: </span>
                      {u.roles.map((r) => r.label).join(', ') || '—'}
                    </p>

                    {/* As entidades ficam visíveis na linha, não escondidas na
                        edição: acesso concedido por engano é invisível de outro
                        jeito, e é o erro mais caro desta tela. */}
                    <p className="mt-0.5 text-xs">
                      <span className="text-slate-400">Enxerga: </span>
                      {u.entity_access.length === 0 ? (
                        <span className="text-rose-600 dark:text-rose-400">
                          nenhuma entidade — este usuário não vê nada no sistema
                        </span>
                      ) : (
                        u.entity_access
                          .map(
                            (a) =>
                              `${a.entity_name} (${a.access_level}${a.can_see_private ? ', vê privados' : ''})`,
                          )
                          .join(' · ')
                      )}
                    </p>

                    <p className="mt-0.5 text-[11px] text-slate-400">
                      {u.last_login_at
                        ? `Último acesso: ${new Date(u.last_login_at).toLocaleString('pt-BR')}`
                        : 'Nunca acessou'}
                    </p>
                  </div>

                  <div className="flex shrink-0 flex-wrap gap-2 text-xs">
                    <button onClick={() => setEditing(u)} className="text-slate-500 dark:text-slate-400 hover:text-indigo-600">
                      Editar
                    </button>
                    <button
                      onClick={() => setResetting(u)}
                      className="text-slate-500 dark:text-slate-400 hover:text-indigo-600"
                    >
                      Redefinir senha
                    </button>
                    {/* Excluir a si mesmo é recusado no servidor; esconder o
                        botão evita a frustração de tentar. */}
                    {!isSelf && (
                      <button
                        onClick={() => {
                          if (
                            confirm(
                              `Desativar ${u.name}? Ele perde o acesso imediatamente. O histórico de ações dele é preservado.`,
                            )
                          ) {
                            remove.mutate(u.id);
                          }
                        }}
                        className="text-slate-500 dark:text-slate-400 hover:text-rose-600"
                      >
                        Excluir
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {(creating || editing) && (
        <UserForm
          user={editing}
          roles={roles}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            invalidate();
          }}
        />
      )}

      {resetting && (
        <PasswordDialog
          mode="admin"
          targetName={resetting.name}
          targetId={resetting.id}
          onClose={() => setResetting(null)}
          onDone={() => setResetting(null)}
        />
      )}

      {ownPassword && (
        <PasswordDialog mode="self" onClose={() => setOwnPassword(false)} onDone={() => setOwnPassword(false)} />
      )}
    </div>
  );
}
